import React, { useMemo, useState, useEffect } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import { useAimSnapshot, useAimSearchLift, useAimSentiment, useAimRuns } from '../lib/aimQueries';
import { colorFor, prettifyProviderLabel } from '../lib/providerColors';
import { bucketFor } from '../lib/ratingScale';
import ProviderColorDot from '../primitives/ProviderColorDot';
import InsightCard from '../primitives/InsightCard';
import AimEmpty, { AimLoading, AimError } from '../primitives/AimEmpty';
import InfoTooltip from '../primitives/InfoTooltip';
import PeriodLabel from '../primitives/PeriodLabel';
import { buildSearchLiftInsight, fmtLiftRatio } from '../lib/insights';
import { multiTurnTitle } from '../lib/runLabel';
import {
  searchLiftModes, isCollectionFailed, isPartial, NO_DATA_MESSAGE, PARTIAL_DATA_MESSAGE,
} from '../lib/dataStatus';
import { isPending } from '../lib/queryState';
import { shortenProviderTick, pickLabelMode } from '../lib/providerTick';

const TIER_LABELS = {
  search_dependent: { label: 'Search Dependent', className: 'bg-red-100 text-red-900 dark:bg-red-700 dark:text-white' },
  partially_known: { label: 'Partially Known', className: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-600 dark:text-white' },
  well_known: { label: 'Well Known', className: 'bg-green-100 text-green-900 dark:bg-green-700 dark:text-white' },
};

function classifyDecay(t1, t2, t3) {
  if ([t1, t2, t3].some((v) => v == null)) return 'unknown';
  if (t3 >= 75) return 'persistent';
  if (t1 - t2 < 10 && t2 - t3 >= 30) return 'late_drop';
  if (t1 - t2 >= 30) return 'early_drop';
  if (t3 > t1) return 'recovery';
  return 'gradual_decay';
}

const CURVE_LABELS = {
  persistent: 'Persistent',
  late_drop: 'Late Drop',
  early_drop: 'Early Drop',
  recovery: 'Recovery',
  gradual_decay: 'Gradual Decay',
  unknown: 'Unknown',
};

function providerDisplayName(providerKey) {
  if (providerKey === 'openai') return 'ChatGPT';
  if (providerKey === 'anthropic') return 'Claude';
  if (providerKey === 'google_ai') return 'Gemini';
  return providerKey;
}

/**
 * The modes of a Search Lift row with nothing to draw: collection failed (the row's own
 * `search_*_status`), or a null rate, which the backend sends for a failed mode and which must
 * never read as 0%.
 *
 * @param {object} r a Search Lift row
 * @returns {{on: boolean, off: boolean}}
 */
function unmeasuredModes(r) {
  const failed = searchLiftModes(r);
  return { on: failed.on || r.search_on_pct == null, off: failed.off || r.search_off_pct == null };
}

/**
 * Search Lift, from the lift RPC alone: each row carries a status for its Search on and Search off
 * paths (`search_on_status`, `search_off_status`) and a `data_status` for the family. A failed path
 * has a null rate, and `lift_ratio` and `knowledge_tier` are null unless both paths answered.
 */
function SearchLift({ clientId, runId }) {
  const { data: rows, loading } = useAimSearchLift(clientId, runId);
  // Pass through the natural backend field names so searchLiftRatio can
  // recompute from the same raw percentages the detail row uses. Renaming
  // these to search_on / search_off used to make the callout fall through
  // to the pre-rounded lift_ratio and disagree with the row (markup #107).
  const insight = buildSearchLiftInsight(Array.isArray(rows) ? rows
    .filter((r) => {
      const unmeasured = unmeasuredModes(r);
      return !unmeasured.on && !unmeasured.off;
    })
    .map((r) => ({
      provider_name: providerDisplayName(r.provider),
      search_on_pct: r.search_on_pct,
      search_off_pct: r.search_off_pct,
      lift_ratio: r.lift_ratio,
    })) : []);

  if (loading) return <AimLoading height={200} />;
  if (!Array.isArray(rows) || rows.length === 0) return <AimEmpty message="No search lift data for this run." />;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Search Lift Ratio</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
          Mention rate when each provider&apos;s live web search is on vs off. Lift = on ÷ off. A high lift means the provider needs live search to surface you; a low or sub-1 lift means it already knows you from training data.
        </p>
        <div className="space-y-2">
          {rows.map((r) => {
            const name = providerDisplayName(r.provider);
            const { on: onNoData, off: offNoData } = unmeasuredModes(r);
            const on = onNoData ? null : Number(r.search_on_pct);
            const off = offNoData ? null : Number(r.search_off_pct);
            // Backend `lift_ratio` ships pre-rounded (e.g. 86/82 arrives as 1.00),
            // so derive from the raw percentages. Round on/off first so the
            // ratio matches the whole-number bars shown next to it: bars 86 / 82
            // → 1.0488 → 1.05, instead of 85.6 / 82.3 → 1.04.
            const answered = !onNoData && !offNoData;
            const offWhole = answered ? Math.round(off) : 0;
            const lift = offWhole > 0
              ? Math.round(on) / offWhole
              : (answered && r.lift_ratio != null ? Number(r.lift_ratio) : null);
            const tier = TIER_LABELS[r.knowledge_tier] || { label: r.knowledge_tier, className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
            return (
              <div key={name} className="text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="inline-flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                    <ProviderColorDot displayName={`${name} (search on)`} /> {name}
                  </span>
                  <span className="inline-flex items-center gap-2 text-xs">
                    {/* The backend leaves the tier null unless both paths answered. */}
                    {r.knowledge_tier != null && answered && (
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${tier.className}`}>{tier.label}</span>
                    )}
                    <span className="text-gray-500 dark:text-gray-400">
                      {!answered ? 'No data' : lift != null ? `${fmtLiftRatio(lift)}× lift` : 'n/a'}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2 pl-[18px]">
                  <span className="w-20 shrink-0 text-[11px] text-gray-500 dark:text-gray-400">Search on</span>
                  <div className="flex-1 h-3 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    {!onNoData && (
                      <div className="h-full" style={{ width: `${Math.min(100, on)}%`, backgroundColor: colorFor(`${name} (search on)`).hex }} />
                    )}
                  </div>
                  {onNoData ? (
                    <span className="w-12 text-right text-xs text-gray-500 dark:text-gray-400">No data</span>
                  ) : (
                    <span className="w-12 text-right text-xs tabular-nums" style={{ color: colorFor(`${name} (search on)`).hex }}>{on.toFixed(0)}%</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 pl-[18px]">
                  <span className="w-20 shrink-0 text-[11px] text-gray-500 dark:text-gray-400">Search off</span>
                  <div
                    className="flex-1 h-3 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden border-dashed border border-gray-300 dark:border-gray-700"
                  >
                    {!offNoData && (
                      <div
                        className="h-full opacity-50"
                        style={{ width: `${Math.min(100, off)}%`, backgroundColor: colorFor(`${name} (search off)`).hex }}
                      />
                    )}
                  </div>
                  {offNoData ? (
                    <span className="w-12 text-right text-xs text-gray-500 dark:text-gray-400">No data</span>
                  ) : (
                    <span className="w-12 text-right text-xs tabular-nums" style={{ color: colorFor(`${name} (search off)`).hex }}>{off.toFixed(0)}%</span>
                  )}
                </div>
                {isPartial(r) && (
                  <p data-testid="search-lift-partial" className="mt-1 pl-[18px] text-[11px] text-gray-500 dark:text-gray-400">
                    {PARTIAL_DATA_MESSAGE}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center gap-4 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block w-6 h-2 rounded bg-gray-400 dark:bg-gray-500" />
            Solid bar = Search on
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block w-6 h-2 rounded border border-dashed border-gray-400 dark:border-gray-500 opacity-60" />
            Dashed bar = Search off
          </span>
        </div>
      </div>
      {insight && <InsightCard {...insight} />}
    </div>
  );
}

function currentLabelMode() {
  return pickLabelMode(typeof window !== 'undefined' ? window.innerWidth : 0);
}

function SentimentComposite({ clientId, runId }) {
  const { data: rows, loading } = useAimSentiment(clientId, runId);
  const [expanded, setExpanded] = useState(null);
  // Always render provider labels under the bars (markup #78 — a prior fix
  // hid them entirely below 1200px which read as a regression). Below the
  // angled threshold we tilt the labels and shorten long names so they
  // don't overflow into adjacent bars. Lazy-init so the first paint already
  // matches the viewport instead of flickering through 'horizontal'.
  const [labelMode, setLabelMode] = useState(currentLabelMode);

  useEffect(() => {
    const update = () => setLabelMode(currentLabelMode());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const isAngled = labelMode === 'angled' || labelMode === 'angled-narrow';
  const isNarrow = labelMode === 'angled-narrow';
  // Truncation budget: narrower viewports get shorter labels. The horizontal
  // mode (>=1800px) has plenty of space and renders the raw display name.
  // Tightened from 12/18 → 10/14 because the angled labels were still
  // crashing into each other at common laptop widths.
  const maxLabelChars = isNarrow ? 10 : isAngled ? 14 : null;
  // Always run the tick through the prettifier so "(search on/off)" is title-
  // cased on the axis even in horizontal mode (markup #108 follow-up); the
  // ellipsis-truncation kicks in only when maxLabelChars is set.
  const tickFormatter = (value) => shortenProviderTick(value, maxLabelChars);

  if (loading) return <AimLoading height={220} />;
  if (!Array.isArray(rows) || rows.length === 0) return <AimEmpty message="No Sentiment data for this run." />;
  const data = rows
    .map((r) => ({
      name: r.display_name,
      sentiment: Number(r.sentiment ?? 0),
      raw: r,
    }))
    // Order bars highest → lowest sentiment.
    .sort((a, b) => b.sentiment - a.sentiment);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 max-[499px]:px-2">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white max-[499px]:px-2">Sentiment Composite</h3>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3 max-[499px]:px-2">
        How positively each provider talks about your property. 100 = uniformly positive language; 0 = neutral or negative. Tracks tone, not factual accuracy.
      </p>
      <ResponsiveContainer width="100%" height={isAngled ? 280 : 240}>
        <BarChart data={data} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
          <CartesianGrid stroke="rgba(148,163,184,0.2)" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            tickFormatter={tickFormatter}
            interval={0}
            angle={isAngled ? -25 : 0}
            textAnchor={isAngled ? 'end' : 'middle'}
            height={isAngled ? 80 : 30}
          />
          <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
          <ThemedChartTooltip labelFormatter={prettifyProviderLabel} />
          <Bar
            dataKey="sentiment"
            name="Sentiment"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={(bar) => {
              const name = bar?.payload?.name ?? bar?.name;
              if (name) setExpanded(name);
            }}
          >
            {data.map((d) => (
              <Cell key={d.name} fill={bucketFor(d.sentiment).hex} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {expanded && (() => {
        const row = rows.find((r) => r.display_name === expanded);
        if (!row) return null;
        // Spec §3B: render the 5 sub-scores as a radar. Hedging/caveat/warning
        // are "lower is better" — invert to keep the shape pointing outward
        // for a healthy signal.
        const sentimentScore = row.sentiment != null ? Number(row.sentiment) : 0;
        const radarFill = bucketFor(sentimentScore).hex;
        // Preserve null on every sub-score so Recharts paints a gap on the
        // radar instead of collapsing the axis to 0 — the right signal for
        // "no client mentions, sentiment unmeasured".
        const nv = (k) => (row[k] != null ? Number(row[k]) : null);
        const inv = (k) => (row[k] != null ? 100 - Number(row[k]) : null);
        const radarData = [
          { axis: 'Avg Rec', value: nv('avg_rec') },
          { axis: 'Superlative', value: nv('superlative_pct') },
          { axis: 'Hedging (inv)', value: inv('hedging_pct') },
          { axis: 'Caveat (inv)', value: inv('caveat_pct') },
          { axis: 'Warning (inv)', value: inv('warning_pct') },
        ];
        const caveats = Array.isArray(row.caveat_texts) ? row.caveat_texts : [];
        const warnings = Array.isArray(row.warning_texts) ? row.warning_texts : [];
        return (
          <div className="mt-3 rounded border border-gray-200 dark:border-gray-800 p-3 text-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-gray-900 dark:text-white">{prettifyProviderLabel(row.display_name)}</div>
              <button type="button" className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" onClick={() => setExpanded(null)}>Close</button>
            </div>
            <div className="w-full md:w-1/2 mx-auto" style={{ height: 180 }}>
              <ResponsiveContainer>
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid />
                  <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar dataKey="value" stroke={radarFill} fill={radarFill} fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            {caveats.length > 0 && (
              <details open={caveats.length <= 3} className="mb-2">
                <summary className="cursor-pointer text-xs uppercase tracking-wide text-amber-600 dark:text-amber-400">
                  ⚠ Caveats mentioned ({caveats.length})
                </summary>
                <ul className="list-disc pl-4 space-y-0.5 text-xs text-gray-700 dark:text-gray-300 mt-1">
                  {caveats.map((t, i) => <li key={i}>“{t}”</li>)}
                </ul>
              </details>
            )}
            {warnings.length > 0 && (
              <details open={warnings.length <= 3}>
                <summary className="cursor-pointer text-xs uppercase tracking-wide text-red-600 dark:text-red-400">
                  🚨 Warnings ({warnings.length})
                </summary>
                <ul className="list-disc pl-4 space-y-0.5 text-xs text-gray-700 dark:text-gray-300 mt-1">
                  {warnings.map((t, i) => <li key={i}>“{t}”</li>)}
                </ul>
              </details>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/**
 * The providers whose P11 rows report conversations left out for a failed turn, with the count.
 *
 * `errored_chains` can differ per turn row, so a provider's count is its largest.
 *
 * @param {Array<{provider_name?: string, provider?: string, errored_chains?: number|string|null}>} breakdown P11 rows
 * @param {string[]} names providers to consider, in the order they are listed
 * @returns {Array<{name: string, errored: number}>} providers with a count above zero
 */
function erroredChainsByProvider(breakdown, names) {
  const max = new Map();
  breakdown.forEach((row) => {
    const name = row.provider_name || row.provider;
    const n = Number(row.errored_chains);
    if (Number.isFinite(n)) max.set(name, Math.max(max.get(name) ?? 0, n));
  });
  return names.map((name) => ({ name, errored: max.get(name) ?? 0 })).filter((p) => p.errored > 0);
}

/**
 * Multi-turn Decay (P11), from the selected run's snapshot.
 *
 * Each row carries `errored_chains` and a `data_status`. A conversation with a failed turn is left
 * out of `chains`, `survived` and `survival_pct`; a path whose every conversation failed has
 * `data_status: 'collection_failed'` and a null `survival_pct`, and is drawn as "No data", never as
 * a 0% curve.
 */
function MultiTurnDecay({ clientId, runId }) {
  const { data: runsData } = useAimRuns(clientId, 20);
  const runs = useMemo(() => (Array.isArray(runsData) ? runsData : []), [runsData]);
  const selectedRun = useMemo(() => runs.find((r) => r.id === runId) || null, [runs, runId]);

  const snapQuery = useAimSnapshot(clientId, 'P11', '', runId);
  const { data: snap, error: eSnap, refresh: rSnap } = snapQuery;
  // Rows: { provider_id, provider_name, turn_number, chains, survived, survival_pct, errored_chains,
  // data_status }. Grouped by provider_name below, never provider_id: a model change gives a
  // provider a new id.
  const breakdown = useMemo(() => (Array.isArray(snap?.value_breakdown)
    ? snap.value_breakdown
    : (snap?.value_breakdown?.providers || [])), [snap]);

  // Providers with a row whose collection failed, in snapshot order, listed under "No data".
  const noDataProviders = useMemo(
    () => [...new Set(breakdown.filter(isCollectionFailed).map((row) => row.provider_name || row.provider))],
    [breakdown],
  );

  const grouped = useMemo(() => {
    const byProvider = new Map();
    breakdown.forEach((row) => {
      const name = row.provider_name || row.provider;
      if (noDataProviders.includes(name)) return;
      if (!byProvider.has(name)) byProvider.set(name, { name, t1: null, t2: null, t3: null });
      const turn = Number(row.turn_number);
      const pct = row.survival_pct == null ? null : Number(row.survival_pct);
      const target = byProvider.get(name);
      if (turn === 1) target.t1 = pct;
      if (turn === 2) target.t2 = pct;
      if (turn === 3) target.t3 = pct;
    });
    const all = Array.from(byProvider.values()).map((p) => ({
      ...p,
      curve_type: classifyDecay(p.t1, p.t2, p.t3),
    }));
    return all;
  }, [breakdown, noDataProviders]);

  const chartData = useMemo(() => {
    return [1, 2, 3].map((turn) => {
      const row = { turn: `Turn ${turn}` };
      grouped.forEach((p) => {
        row[p.name] = p[`t${turn}`];
      });
      return row;
    });
  }, [grouped]);

  // The note covers the providers that are drawn: a provider under "No data" has no curve to qualify.
  const drawnNames = useMemo(() => grouped.map((p) => p.name), [grouped]);
  const erroredChains = useMemo(() => erroredChainsByProvider(breakdown, drawnNames), [breakdown, drawnNames]);
  const partialProviders = useMemo(
    () => drawnNames.filter((name) => breakdown.some((row) => (row.provider_name || row.provider) === name && isPartial(row))),
    [breakdown, drawnNames],
  );

  if (isPending(snapQuery)) return <AimLoading height={240} />;
  if (eSnap) return <AimError message="Multi-turn decay data unavailable." onRetry={rSnap} />;
  if (grouped.length === 0 && noDataProviders.length === 0) {
    return <AimEmpty message="Multi-turn decay (P11) needs at least one chained run." height={160} />;
  }

  const byCurve = grouped.reduce((acc, p) => {
    (acc[p.curve_type] = acc[p.curve_type] || []).push(p.name);
    return acc;
  }, {});

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 max-[499px]:px-2">
      <div className="flex items-center gap-1.5 mb-3 max-[499px]:px-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {`${multiTurnTitle(selectedRun)} (`}
          <InfoTooltip
            label="P11 — Multi-turn Decay"
            trigger={<span className="border-b border-dotted border-gray-400 dark:border-gray-500 cursor-help">P11</span>}
          >
            <strong>P11 — Multi-turn Decay.</strong> How often you stay in the conversation as a chat continues. For each
            provider, the % of 3-turn chains where your park is still mentioned at
            turns 1, 2, and 3. Curves are classified as Persistent, Late Drop,
            Early Drop, Recovery, or Gradual Decay. Collected in monthly full runs
            only.
          </InfoTooltip>
          {')'}
        </h3>
      </div>
      <div className="mb-2 -mt-2 max-[499px]:px-2"><PeriodLabel snapshot={snap} /></div>
      {grouped.length > 0 && (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData}>
            <CartesianGrid stroke="rgba(148,163,184,0.2)" />
            <XAxis dataKey="turn" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
            <ThemedChartTooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {grouped.map((p) => {
              const c = colorFor(p.name);
              return (
                <Line
                  key={p.name}
                  type="monotone"
                  dataKey={p.name}
                  name={prettifyProviderLabel(p.name)}
                  stroke={c.hex}
                  strokeOpacity={c.faded ? 0.5 : 1}
                  dot
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
      {(erroredChains.length > 0 || partialProviders.length > 0) && (
        <div className="mt-2 space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400 max-[499px]:px-2">
          {erroredChains.length > 0 && (
            <p data-testid="decay-errored-chains">
              Excludes conversations with a failed turn: {erroredChains.map((p) => `${prettifyProviderLabel(p.name)} (${p.errored})`).join(', ')}
            </p>
          )}
          {partialProviders.length > 0 && (
            <p data-testid="decay-partial">
              {partialProviders.map(prettifyProviderLabel).join(', ')}: {PARTIAL_DATA_MESSAGE}
            </p>
          )}
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
        {Object.entries(byCurve).map(([curve, names]) => (
          <div key={curve} className="rounded border border-gray-200 dark:border-gray-800 p-2">
            <div className="font-semibold text-gray-700 dark:text-gray-300">{CURVE_LABELS[curve] || curve}</div>
            <div className="text-gray-500 dark:text-gray-400">{names.map(prettifyProviderLabel).join(', ')}</div>
          </div>
        ))}
        {noDataProviders.length > 0 && (
          <div data-testid="decay-no-data" className="rounded border border-gray-200 dark:border-gray-800 p-2">
            <div className="font-semibold text-gray-700 dark:text-gray-300">No data</div>
            <div className="text-gray-500 dark:text-gray-400">{noDataProviders.map(prettifyProviderLabel).join(', ')}</div>
            <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">{NO_DATA_MESSAGE}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProvidersTab({ clientId, runId, onSelectProvider }) {
  return (
    <div className="space-y-5">
      <SearchLift clientId={clientId} runId={runId} />
      <SentimentComposite clientId={clientId} runId={runId} />
      <MultiTurnDecay clientId={clientId} runId={runId} />
    </div>
  );
}
